// src/oura/ui/glide.ts
//
// One highlight that glides to the item you are on, shared by the sidebar
// rail and the merchant's phone bar, so a page change moves the highlight
// instead of making it jump.
//
// Both navs are mounted afresh on every page change (each route renders its
// own screen), so a highlight's last place is kept here, outside any
// component, one per nav. A new mount paints the highlight at the old item
// and lets the CSS transition carry it to the new one. A first visit has no
// last place, and a transition never runs on an element's first paint, so a
// page load does not show the highlight flying in.

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

/** Offsets inside the nav's positioned container, in px. */
export interface GlideMark {
    x: number;
    y: number;
    w: number;
    h: number;
}

const LAST_MARK = new Map<string, GlideMark>();

const same = (a: GlideMark, b: GlideMark) => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/**
 * Measures the element `selector` finds inside `container` and returns where
 * the highlight goes, plus the ref to put on the highlight element. The
 * container must be the target's offset parent (positioned), and the
 * highlight an absolutely placed child of it at top-left 0 that takes the
 * returned position as a `translate`.
 *
 * `remeasure` re-runs the measure when something the selector cannot see
 * moves the target (the item list itself).
 */
export function useGlideMark(container: RefObject<HTMLElement | null>, selector: string | null, memoryKey: string, remeasure: unknown) {
    const markRef = useRef<HTMLSpanElement>(null);
    const mounted = useRef(false);
    const glideFrom = useRef<GlideMark | null>(null);
    const [mark, setMark] = useState<GlideMark | null>(null);

    useLayoutEffect(() => {
        const el = selector ? container.current?.querySelector<HTMLElement>(selector) : null;
        if (!el) {
            setMark(null);
            return;
        }
        const next: GlideMark = { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight };
        const last = LAST_MARK.get(memoryKey);
        LAST_MARK.set(memoryKey, next);
        if (!mounted.current && last && !same(last, next)) glideFrom.current = last;
        mounted.current = true;
        setMark((was) => (was && same(was, next) ? was : next));
    }, [container, selector, memoryKey, remeasure]);

    // The target can move without a render of this component: the phone bar
    // going from `display: none` to shown when a window narrows, the bell
    // arriving after the business loads and narrowing every tab, a web font
    // landing. The box and its children are watched, so a sibling that
    // changes the layout is seen through the children it resizes. Followed
    // without a glide-from (the transition still eases the move).
    useEffect(() => {
        const box = container.current;
        if (!box || !selector || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => {
            const el = box.querySelector<HTMLElement>(selector);
            if (!el) return;
            const next: GlideMark = { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight };
            LAST_MARK.set(memoryKey, next);
            setMark((was) => (was && same(was, next) ? was : next));
        });
        ro.observe(box);
        for (const child of Array.from(box.children)) ro.observe(child);
        return () => ro.disconnect();
    }, [container, selector, memoryKey]);

    // Set on the element before the first paint, so there is no frame at
    // either end without it.
    useLayoutEffect(() => {
        const el = markRef.current;
        const from = glideFrom.current;
        if (!el || !mark || !from) return;
        glideFrom.current = null;
        el.style.transition = "none";
        el.style.transform = `translate(${from.x}px, ${from.y}px)`;
        void el.offsetHeight; // commit the start position
        el.style.transition = "";
        el.style.transform = `translate(${mark.x}px, ${mark.y}px)`;
    }, [mark]);

    return { mark, markRef };
}

/** Apple's ease-out for UI that settles without overshoot (the drawer curve). */
export const SETTLE = "ease-[cubic-bezier(0.32,0.72,0,1)]";

/** The highlight's own classes: placed at top-left, glides, still under reduced motion. */
export const GLIDE = `pointer-events-none absolute left-0 top-0 transition-transform duration-300 motion-reduce:transition-none ${SETTLE}`;
